import { Button, Flex, Typography } from 'antd';
import { useFlags } from 'launchdarkly-react-client-sdk';

import AccountDescriptions from './AccountDescriptions';
import AccountTransferHistoryModal from './AccountTransferHistoryModal';
import CardShippingEventsModal from './CardShippingEventsModal';
import DigitalWalletModal from './DigitalWalletModal';
import useModalControls from 'hooks/useModalControls';
import { formatDate } from 'utilities/datesAndTimes';
import { CARD_ISSUANCE_STATE_STATUS } from 'utilities/constants';

export const DIGITAL_WALLET_STATUS = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  NONE: 'No History',
};

const CardDetailsDescriptions = ({ account, simpleAccount }) => {
  const { showCardFullfillment } = useFlags();
  const accountTransferHistoryModal = useModalControls();
  const cardShippingEventsModal = useModalControls();
  const digitalWalletModal = useModalControls();

  const issuanceState = account?.cardDetails?.issuanceState;
  const cardIsActivated =
    issuanceState === CARD_ISSUANCE_STATE_STATUS.ACTIVATED ||
    issuanceState === CARD_ISSUANCE_STATE_STATUS.PRE_EXPIRED; // PRE_EXPIRED means card is within 90 days of expiration, but still activated

  const digitalWalletInfo = account?.digitalWallet?.info;
  const cardShipping = account?.cardShipping;
  const sponsorBank = account?.cardDetails?.bank;

  const items = [];

  items.push({
    key: 1,
    span: 2,
    label: 'Last 4',
    children: (
      <Flex
        align="center"
        justify="space-between"
        gap="var(--spacing-xxs)"
        wrap="wrap"
      >
        <div data-testid="last-4">{account?.cardDetails?.last4}</div>
        <Button
          type="link"
          size="small"
          onClick={accountTransferHistoryModal.show}
          style={{ padding: 0 }}
        >
          View History
        </Button>
      </Flex>
    ),
  });

  items.push({
    key: 2,
    span: 2,
    label: 'Opened Date',
    children: (
      <span data-testid="opened-date">
        {simpleAccount?.createdAt ? formatDate(simpleAccount.createdAt) : ''}
      </span>
    ),
  });

  items.push({
    key: 3,
    span: 2,
    label: 'Card Expiration Date',
    children: (
      <span data-testid="card-expiration-date">
        {formatDate(account?.cardDetails?.expirationDate)}
      </span>
    ),
  });

  if (showCardFullfillment) {
    const mostRecentEvent = cardShipping?.mostRecentEvent;
    items.push({
      key: 9,
      span: 2,
      label: 'Card Fulfillment Date',
      children: (
        <span data-testid="card-fulfillment-date">
          {mostRecentEvent ? (
            <Flex
              align="center"
              justify="space-between"
              gap="var(--spacing-xxs)"
              wrap="wrap"
            >
              <span data-testid="most-recent-fulfillment-date">
                {formatDate(mostRecentEvent.date)}{' '}
                {mostRecentEvent.courier ? '- shipped' : '- created'}
              </span>

              <Button
                onClick={cardShippingEventsModal.show}
                type="link"
                style={{ padding: 0 }}
              >
                View Details
              </Button>
            </Flex>
          ) : (
            ''
          )}
        </span>
      ),
    });
  }

  items.push({
    key: 4,
    span: 2,
    label: 'Card Activation Date',
    children: (
      <span data-testid="card-activation-date">
        {cardIsActivated
          ? formatDate(account?.cardDetails?.cardReceiptVerificationDate)
          : 'Not Activated'}
      </span>
    ),
  });

  items.push({
    key: 5,
    span: 2,
    label: 'Last Reissue Date',
    children: (
      <span data-testid="last-reissue-date">
        {formatDate(account?.cardDetails?.lastCardRequestDate)}
      </span>
    ),
  });

  if (digitalWalletInfo) {
    const resolvedStatus = DIGITAL_WALLET_STATUS[digitalWalletInfo.status];

    items.push({
      key: 6,
      span: 2,
      label: 'Digital Wallet',
      children: (
        <Flex
          align="center"
          justify="space-between"
          gap="var(--spacing-xxs)"
          wrap="wrap"
        >
          <span data-testid="digital-wallet-status">
            {resolvedStatus || (
              <Typography.Text type="danger">Failed to fetch</Typography.Text>
            )}
          </span>
          {resolvedStatus ? (
            <Button
              onClick={digitalWalletModal.show}
              type="link"
              style={{ padding: 0 }}
            >
              View Details
            </Button>
          ) : undefined}
        </Flex>
      ),
    });
  }

  if (sponsorBank) {
    items.push({
      key: 7,
      span: 2,
      label: 'Sponsor Bank',
      children: <span data-testid="sponsor-bank">{sponsorBank}</span>,
    });
  }

  return (
    <>
      <AccountDescriptions title="Card Details" items={items} />
      <AccountTransferHistoryModal {...accountTransferHistoryModal} />
      <CardShippingEventsModal
        {...cardShippingEventsModal}
        events={cardShipping?.events}
      />
      <DigitalWalletModal {...digitalWalletModal} />
    </>
  );
};

export default CardDetailsDescriptions;
